import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HandTracking from './components/HandTracking';
import Card3D from './components/Card3D';
import Three3DCards from './components/Three3DCards';
import { cards, getRandomCard } from './data/CardContent';

const GESTURE_COOLDOWN = 500;
const PEACE_HOLD_TIME = 1000;

const gestureInstructions = {
  open_palm: { icon: '🖐️', text: '手掌 - 移动控制旋转', color: 'text-cyan-400' },
  peace: { icon: '✌️', text: '剪刀手 - 保持1秒抽卡', color: 'text-yellow-400' },
  fist: { icon: '✊', text: '拳头 - 重置', color: 'text-red-400' },
  unknown: { icon: '👋', text: '请面对摄像头', color: 'text-gray-400' }
};

const DebugPanel = ({ currentGesture, handPosition, isTracking, rotationSpeed, holdProgress, card }) => {
  const instruction = gestureInstructions[currentGesture] || gestureInstructions.unknown;
  
  return (
    <div className="fixed top-4 right-4 bg-black/80 backdrop-blur-sm rounded-xl p-4 text-xs font-mono z-50 max-w-xs border border-white/10">
      <h3 className="text-white font-bold mb-3 border-b border-gray-600 pb-2">🔧 调试面板</h3>
      
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-gray-400">状态:</span>
          <span className={isTracking ? 'text-green-400' : 'text-red-400'}>
            {isTracking ? '● 追踪中' : '○ 未追踪'}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">手势:</span>
          <span className={instruction.color}>{instruction.text}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">旋转速度:</span>
          <span className="text-cyan-400">{rotationSpeed.toFixed(1)}°/帧</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">保持进度:</span>
          <span className="text-yellow-400">{holdProgress}%</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">card:</span>
          <span className={card ? 'text-green-400' : 'text-gray-500'}>
            {card ? `#${card.id}` : 'null'}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">X 坐标:</span>
          <span className="text-cyan-400">{handPosition.x.toFixed(3)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-400">Y 坐标:</span>
          <span className="text-cyan-400">{handPosition.y.toFixed(3)}</span>
        </div>
      </div>
    </div>
  );
};

const RotatingCards = ({ rotation, rotationSpeed, onCardSelect, isPeaceHolding, holdProgress }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const displayCards = cards.slice(0, 8);
  
  return (
    <div className="relative w-[500px] h-[500px] flex items-center justify-center">
      {displayCards.map((card, index) => {
        const angle = (index / displayCards.length) * 360 + rotation;
        const radius = 160;
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const y = Math.sin((angle * Math.PI) / 180) * radius;
        const baseScale = Math.abs(Math.cos((angle * Math.PI) / 180)) * 0.4 + 0.6;
        const zIndex = Math.floor(baseScale * 20);
        const isHovered = hoveredCard === card.id;
        const isSelecting = isPeaceHolding;
        
        return (
          <motion.div
            key={card.id}
            className="absolute cursor-pointer"
            style={{
              left: `calc(50% + ${x}px - 64px)`,
              top: `calc(50% + ${y}px - 96px)`,
              zIndex: isHovered ? 100 : zIndex,
            }}
            animate={{
              scale: isHovered ? baseScale * 1.4 : baseScale,
              rotate: angle,
              zIndex: isHovered ? 100 : zIndex,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 20
            }}
            whileHover={{ scale: baseScale * 1.4, zIndex: 100 }}
            whileTap={{ scale: baseScale * 1.2 }}
            onHoverStart={() => setHoveredCard(card.id)}
            onHoverEnd={() => setHoveredCard(null)}
            onClick={() => onCardSelect(card)}
          >
            <div className={`w-32 h-48 rounded-xl bg-gradient-to-br ${card.theme.gradient} 
                          flex flex-col items-center justify-center p-2 text-white
                          shadow-lg border-2 ${isSelecting ? 'border-yellow-400/80 shadow-yellow-400/30' : isHovered ? 'border-white/60' : 'border-white/20'}
                          transition-all duration-300`}>
              {isSelecting && (
                <motion.div 
                  className="absolute inset-0 rounded-xl overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div 
                    className="absolute bottom-0 left-0 right-0 h-full bg-yellow-400/30"
                    initial={{ height: '0%' }}
                    animate={{ height: `${holdProgress}%` }}
                    transition={{ duration: 100 }}
                  />
                </motion.div>
              )}
              
              <motion.span 
                className="text-3xl mb-1 block"
                animate={{ scale: isHovered ? 1.3 : 1 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {card.icon}
              </motion.span>
              <span className="text-sm font-bold text-center leading-tight">{card.theme.name}</span>
              <span className="text-[10px] opacity-60 mt-1">#{card.id}</span>
            </div>
          </motion.div>
        );
      })}
      
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <motion.div 
          className={`w-24 h-24 rounded-full flex items-center justify-center ${
            isPeaceHolding 
              ? 'bg-yellow-500/60 animate-pulse' 
              : rotationSpeed > 2 
                ? 'bg-cyan-500/60 animate-ping' 
                : 'bg-purple-500/30'
          }`}
          animate={{
            scale: [1, 1.2, 1],
            rotate: rotation
          }}
          transition={{
            scale: { duration: 0.5, repeat: Infinity },
            rotate: { duration: 0, repeat: 0 }
          }}
        >
          <span className="text-3xl">{isPeaceHolding ? '🎯' : rotationSpeed > 2 ? '💨' : '🎴'}</span>
        </motion.div>
      </div>
    </div>
  );
};

const InstructionBanner = ({ currentGesture, holdProgress, isPeaceHolding }) => {
  const instruction = gestureInstructions[currentGesture] || gestureInstructions.unknown;
  
  return (
    <motion.div
      className="fixed top-24 left-1/2 -translate-x-1/2 z-30"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className={`flex items-center gap-3 px-6 py-4 rounded-full bg-black/70 backdrop-blur-sm
                    border border-white/20 shadow-lg ${isPeaceHolding ? 'border-yellow-400' : ''}`}>
        {isPeaceHolding ? (
          <motion.div 
            className="w-8 h-8 rounded-full bg-yellow-400"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 0.5, repeat: Infinity }}
          />
        ) : (
          <span className="text-4xl animate-bounce">{instruction.icon}</span>
        )}
        <div className="flex flex-col">
          <span className={`text-xl font-bold ${instruction.color}`}>
            {instruction.text}
          </span>
          {isPeaceHolding && (
            <motion.div 
              className="w-32 h-2 bg-gray-700 rounded-full mt-1 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div 
                className="h-full bg-yellow-400 rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: `${holdProgress}%` }}
                transition={{ duration: 100, ease: "linear" }}
              />
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

function App() {
  const [currentGesture, setCurrentGesture] = useState('unknown');
  const [handPosition, setHandPosition] = useState({ x: 0.5, y: 0.5 });
  const [prevHandPosition, setPrevHandPosition] = useState({ x: 0.5, y: 0.5 });
  const [isTracking, setIsTracking] = useState(false);
  const [card, setCard] = useState(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [rotationSpeed, setRotationSpeed] = useState(1);
  const [isPeaceHolding, setIsPeaceHolding] = useState(false);
  const [holdStartTime, setHoldStartTime] = useState(null);
  const [holdProgress, setHoldProgress] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const handTrackingRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  const lastGestureTimeRef = useRef(0);
  const lastGestureRef = useRef('unknown');
  const stateRef = useRef({
    isSelecting: false,
    card: null,
    isPeaceHolding: false
  });

  useEffect(() => {
    stateRef.current.isPeaceHolding = isPeaceHolding;
    stateRef.current.card = card;
  }, [isPeaceHolding, card]);

  const handleGestureChange = useCallback((gesture) => {
    const now = Date.now();
    
    if (gesture === lastGestureRef.current) {
      return;
    }
    
    if (now - lastGestureTimeRef.current < GESTURE_COOLDOWN) {
      return;
    }
    
    lastGestureRef.current = gesture;
    lastGestureTimeRef.current = now;
    
    setCurrentGesture(gesture);
    console.log('🎯 检测到手势:', gesture, '| card:', stateRef.current.card ? `#${stateRef.current.card.id}` : 'null', '| isPeaceHolding:', stateRef.current.isPeaceHolding);
    
    switch (gesture) {
      case 'fist':
        setCard(null);
        setIsFlipped(false);
        setShowWelcome(true);
        setRotation(0);
        setRotationSpeed(1);
        setIsPeaceHolding(false);
        setHoldStartTime(null);
        setHoldProgress(0);
        setCurrentGesture('unknown');
        console.log('✅ 拳头重置');
        break;
        
      case 'peace':
        if (!stateRef.current.card && !stateRef.current.isPeaceHolding) {
          setIsPeaceHolding(true);
          setHoldStartTime(now);
          setHoldProgress(0);
          console.log('✌️ 开始保持剪刀手');
        }
        break;
        
      default:
        break;
    }
  }, []);

  const handlePositionChange = useCallback((position) => {
    setHandPosition(position);
    setIsTracking(true);
    
    if (lastGestureRef.current === 'open_palm') {
      const deltaX = position.x - prevHandPosition.x;
      const movement = Math.abs(deltaX);
      const speed = Math.min(movement * 100, 10);
      
      if (deltaX < -0.01) {
        setRotationSpeed(Math.max(0.5, 1 - speed * 0.5));
        console.log('⬅️ 向左挥手 - 减速:', rotationSpeed);
      } else if (deltaX > 0.01) {
        setRotationSpeed(Math.min(10, 1 + speed * 2));
        console.log('➡️ 向右挥手 - 加速:', rotationSpeed);
      } else {
        setRotationSpeed(Math.max(1, rotationSpeed * 0.98));
      }
    }
    
    setPrevHandPosition(position);
  }, [prevHandPosition]);

  const handleCardSelect = useCallback((selectedCard) => {
    setCard(selectedCard);
    setIsFlipped(true);
    setShowWelcome(false);
    setIsPeaceHolding(false);
    setHoldStartTime(null);
    setHoldProgress(0);
    setRotationSpeed(1);
    console.log('🖱️ 点击卡片:', selectedCard.title);
  }, []);

  useEffect(() => {
    const animate = () => {
      setRotation(prev => (prev + rotationSpeed) % 360);
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    if (!card) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [card, rotationSpeed]);

  useEffect(() => {
    let holdInterval;
    
    if (isPeaceHolding && holdStartTime) {
      holdInterval = setInterval(() => {
        const elapsed = Date.now() - holdStartTime;
        const progress = Math.min((elapsed / PEACE_HOLD_TIME) * 100, 100);
        setHoldProgress(progress);
        
        if (elapsed >= PEACE_HOLD_TIME) {
          const newCard = getRandomCard();
          setCard(newCard);
          setIsFlipped(true);
          setShowWelcome(false);
          setIsPeaceHolding(false);
          setHoldStartTime(null);
          setHoldProgress(0);
          console.log('✅ 剪刀手保持1秒 - 抽卡成功！卡片:', newCard.title);
        }
      }, 50);
    }
    
    return () => {
      if (holdInterval) {
        clearInterval(holdInterval);
      }
    };
  }, [isPeaceHolding, holdStartTime]);

  useEffect(() => {
    if (currentGesture !== 'open_palm') {
      setRotationSpeed(1);
    }
  }, [currentGesture]);

  const instruction = gestureInstructions[currentGesture] || gestureInstructions.unknown;
  const isPeaceHoldingDisplay = isPeaceHolding;

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <DebugPanel 
        currentGesture={currentGesture}
        handPosition={handPosition}
        isTracking={isTracking}
        rotationSpeed={rotationSpeed}
        holdProgress={holdProgress}
        card={card}
      />
      
      <InstructionBanner 
        currentGesture={currentGesture} 
        holdProgress={holdProgress}
        isPeaceHolding={isPeaceHoldingDisplay}
      />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="p-4 md:p-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <motion.h1 
              className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              🎴 手势抽卡 3D
            </motion.h1>
            
            <motion.div 
              className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                isTracking ? 'bg-green-500/20 border border-green-500/50' : 'bg-red-500/20 border border-red-500/50'
              }`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className={`w-3 h-3 rounded-full ${isTracking ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
              <span className="text-sm text-gray-200">
                {isTracking ? '🟢 摄像头已启动' : '🔴 正在初始化...'}
              </span>
            </motion.div>
          </div>
        </header>

        <main className="flex-1 flex flex-col items-center justify-center p-4">
          <motion.div 
            className="mb-8 text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-4 mb-4">
              <motion.span 
                className="text-7xl"
                animate={{ 
                  scale: isPeaceHoldingDisplay ? [1, 1.2, 1] : [1, 1.1, 1],
                  rotate: currentGesture === 'open_palm' ? [0, 5, -5, 0] : 0
                }}
                transition={{ 
                  scale: { duration: isPeaceHoldingDisplay ? 0.3 : 1.5, repeat: Infinity },
                  rotate: { duration: 0.5, repeat: Infinity }
                }}
              >
                {isPeaceHoldingDisplay ? '✌️' : instruction.icon}
              </motion.span>
              <div className="text-left">
                <motion.p 
                  className={`text-2xl font-bold ${instruction.color}`}
                  animate={isPeaceHoldingDisplay ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  {isPeaceHoldingDisplay ? `保持中... ${Math.ceil((100 - holdProgress) / 10)}` : instruction.text}
                </motion.p>
                <p className="text-sm text-gray-400 mt-1">
                  {currentGesture === 'open_palm' ? '挥动双手加速旋转' : '当前手势'}
                </p>
              </div>
            </div>
          </motion.div>

          {!card ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="w-full h-[600px]"
            >
              <Three3DCards 
                rotation={rotation}
                speed={rotationSpeed}
                isSelecting={isPeaceHoldingDisplay}
                holdProgress={holdProgress}
                onCardSelect={handleCardSelect}
              />
              
              {showWelcome && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-8 text-center max-w-md mx-auto"
                >
                  <p className="text-gray-300 text-sm leading-relaxed mb-4">
                    🎯 简化交互方式：
                  </p>
                  <div className="grid grid-cols-3 gap-3 text-sm text-gray-300">
                    <div className="bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/30">
                      <span className="text-cyan-400 text-lg block mb-1">🖐️ 手掌</span>
                      <p className="text-xs text-gray-500">移动加速旋转</p>
                    </div>
                    <div className="bg-yellow-500/10 rounded-lg p-3 border border-yellow-500/30">
                      <span className="text-yellow-400 text-lg block mb-1">✌️ 剪刀手</span>
                      <p className="text-xs text-gray-500">保持1秒抽卡</p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30">
                      <span className="text-red-400 text-lg block mb-1">✊ 拳头</span>
                      <p className="text-xs text-gray-500">重置/返回</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-xs mt-4">
                    💡 或直接点击卡片进行抽取
                  </p>
                </motion.div>
              )}
            </motion.div>
          ) : (
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
              >
                <Card3D 
                  card={card}
                  isFlipped={isFlipped}
                  rotation={{ x: 0, y: 0 }}
                  isSelected={true}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="mt-10 flex gap-4 justify-center"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setCard(null);
                    setIsFlipped(false);
                    setRotation(0);
                    setRotationSpeed(1);
                    setShowWelcome(true);
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 rounded-xl font-bold
                           hover:from-purple-600 hover:via-pink-600 hover:to-red-600 transition-all shadow-xl
                           flex items-center gap-3 text-lg"
                >
                  <span className="text-2xl">🎲</span>
                  重新抽卡
                </motion.button>
              </motion.div>
            </div>
          )}
        </main>

        <footer className="p-4 text-center text-gray-500 text-sm">
          <p>🤖 Powered by React + MediaPipe Hands</p>
          <p className="text-xs mt-1">📹 请确保允许摄像头权限 | 右下角可查看摄像头预览</p>
        </footer>
      </div>

      <HandTracking
        ref={handTrackingRef}
        onGestureChange={handleGestureChange}
        onPositionChange={handlePositionChange}
        isActive={true}
      />
    </div>
  );
}

export default App;
